import { z } from "zod";

import { BRAND_MARKUP } from "../../constants";
import { attachment } from "../shared";
import { defineModel } from "../types";

// Video edit: flat rate — $0.975 (basic), $1.875 (high)
const COST_BASIC = 0.975 * 1_000_000 * (1 + BRAND_MARKUP);
const COST_HIGH = 1.875 * 1_000_000 * (1 + BRAND_MARKUP);

const REPLICATE_EDIT_COST_PER_SECOND_HIGH =
  0.29 * 1_000_000 * (1 + BRAND_MARKUP);
const REPLICATE_EDIT_COST_PER_SECOND_BASIC =
  0.22 * 1_000_000 * (1 + BRAND_MARKUP);

const seedance2VideoEditParams = z
  .object({
    aspectRatio: z.enum(["16:9", "9:16", "4:3", "3:4"]).default("9:16"),
    quality: z.enum(["basic", "high"]).default("basic"),
    attachments: z.array(attachment).optional(),
    prompt: z.string().default(""),
  })
  .strip();

export const seedance2VideoEdit = defineModel({
  key: "seedance-2-video-edit",
  stepType: "video",
  spec: {
    label: "Seedance 2.0 Video Edit",
    description: "Edit an existing video using text prompts",
    icon: "seedance-icon.png",
    tags: ["video-gen", "video-edit"],
    featured: true,
    info: "Modify an existing video with text instructions. Connect a video input.",
    features: {
      aspectRatio: true,
      quality: true,
      attachments: true,
      prompts: true,
    } as const,
    options: {
      attachments: [
        {
          type: "video" as const,
          allowedFormats: ["video/mp4"],
          label: "Source Video",
          slot: "source_video",
          required: true,
        },
      ],
      aspectRatio: {
        items: [
          { label: "16:9", value: "16:9" },
          { label: "9:16", value: "9:16" },
          { label: "4:3", value: "4:3" },
          { label: "3:4", value: "3:4" },
        ],
      },
      quality: {
        items: [
          { label: "Basic", value: "basic" },
          { label: "High", value: "high" },
        ],
      },
    },
    params: seedance2VideoEditParams,
  },
  referencePricing: {
    provider: "fal.ai",
    url: "https://fal.ai/models/bytedance/seedance-2.0/reference-to-video",
    entries: [{ unit: "per_generation", unitPriceMicrodollars: 1_814_000 }],
  },
  providers: [
    {
      provider: "replicate",
      modelId: "bytedance/seedance-2.0",
      pricingUnit: "per_generation" as const,
      estimateCost(params) {
        const estimatedDuration = 5;
        const perSecond =
          params.quality === "high"
            ? REPLICATE_EDIT_COST_PER_SECOND_HIGH
            : REPLICATE_EDIT_COST_PER_SECOND_BASIC;
        const perSecondEstimate = Math.ceil(perSecond * estimatedDuration);
        const flatCap = Math.ceil(
          params.quality === "high" ? COST_HIGH : COST_BASIC,
        );
        return Math.min(perSecondEstimate, flatCap);
      },
      costPerSecond: REPLICATE_EDIT_COST_PER_SECOND_BASIC,
      toPayload(params) {
        const videoUrl = params.attachments?.find(
          (att) => att.slot === "source_video",
        )?.data;

        // Replicate's seedance-2.0 (Normal) Cog has a bug where reference_videos
        // fails. Video-edit always uses reference_videos, so force Fast regardless
        // of quality setting. Remove this override when Replicate fixes Normal Cog.
        const model = "bytedance/seedance-2.0-fast" as const;

        return {
          model,
          input: {
            prompt: params.prompt || undefined,
            ...(videoUrl ? { reference_videos: [videoUrl] } : {}),
            resolution: "720p" as const,
            duration: -1,
            aspect_ratio: params.aspectRatio,
            generate_audio: true,
          },
        };
      },
    },
    {
      provider: "muapi",
      modelId: "seedance-v2.0-video-edit",
      estimateCost(params) {
        return Math.ceil(params.quality === "high" ? COST_HIGH : COST_BASIC);
      },
      costPerSecond: COST_BASIC / 5,
      pricingUnit: "per_generation",
      toPayload(params) {
        const videoUrl = params.attachments?.find(
          (att) => att.slot === "source_video",
        )?.data;

        return {
          endpoint: "seedance-v2.0-video-edit" as const,
          prompt: params.prompt || undefined,
          ...(videoUrl ? { video_urls: [videoUrl] } : {}),
          aspect_ratio: params.aspectRatio,
          quality: params.quality,
          remove_watermark: true,
        };
      },
    },
  ],
});
